import axios from 'axios';
import { useEffect, useState } from 'react';

import CookiesPreferences from './CookiesPreferences'; // Import the component
import { CookiePreferencesState } from './CookiesPreferences';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface CookiePreferencesResponse {
  preferences: CookiePreferencesState;
}

const defaultPreferences: CookiePreferencesState = {
  essential: 'true',
  analytics: 'false',
  adsTracking: 'false',
  thirdParty: 'false',
  consent: 'false',
};

const CookieBanner = (): JSX.Element | null => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false); // Toggle preferences view
  const [, setPreferences] = useState<CookiePreferencesState>(defaultPreferences);

  useEffect(() => {
    axios
      .get<CookiePreferencesResponse>(`${backendUrl}/cookies/get-cookie-user-preferences/`, { withCredentials: true })
      .then((response) => {
        if (!response.data.preferences) {
          console.error('Error: preferences is undefined!');
          return;
        }

        setPreferences(response.data.preferences);
        setShowBanner(response.data.preferences.consent !== 'true');
      })
      .catch((error) => console.error('Error fetching preferences:', error));
  }, []);

  const handleAcceptAll = () => {
    const updatedPreferences: CookiePreferencesState = {
      essential: 'true',
      analytics: 'true',
      adsTracking: 'true',
      thirdParty: 'true',
      consent: 'true', // Explicitly mark consent as given
    };

    axios
      .post(`${backendUrl}/cookies/set-cookie-user-preferences/`, JSON.stringify({ preferences: updatedPreferences }), {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        //no need for the csrftoken here because of the interceptor.
      })
      .then(() => {
        setPreferences(updatedPreferences);
        setShowBanner(false);
        window.location.reload();
      })
      .catch((error) => console.error('Error updating preferences:', error));
  };

  const handleDeclineNonEssential = () => {
    const updatedPreferences: CookiePreferencesState = {
      essential: 'true',
      analytics: 'false',
      adsTracking: 'false',
      thirdParty: 'false',
      consent: 'true', // Explicitly mark consent as given
    };

    axios
      .post(`${backendUrl}/cookies/set-cookie-user-preferences/`, JSON.stringify({ preferences: updatedPreferences }), {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      })
      .then(() => {
        setPreferences(updatedPreferences);
        setShowBanner(false);
        window.location.reload();
      })
      .catch((error) => console.error('Error updating preferences:', error));
  };

  return showBanner ? (
    <div
      style={{
        background: 'var(--intermediate-color)',
        color: 'var(--surface-elevated)',
        padding: '15px',
        position: 'fixed',
        bottom: 0,
        width: 'auto',
        textAlign: 'center',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        borderRadius: '10px',
      }}
    >
      {showPreferences ? (
        <CookiesPreferences /> // Show preferences instead of banner
      ) : (
        <>
          <p>We use cookies to enhance your experience. Manage your preferences below.</p>
          <div>
            <button onClick={handleAcceptAll} style={{ marginRight: '10px', padding: '8px 12px', cursor: 'pointer', backgroundColor: 'var(--accent-color)', color: 'var(--surface-elevated)', border: 'none', borderRadius: '5px' }}>
              Accept All
            </button>
            <button onClick={handleDeclineNonEssential} style={{ padding: '8px 12px', cursor: 'pointer', backgroundColor: 'var(--error-bg)', color: 'var(--error-text)', border: 'none', borderRadius: '5px' }}>
              Decline Non-Essential
            </button>
          </div>
          <div>
            <button
              onClick={() => setShowPreferences(true)}
              style={{ marginTop: '5px', cursor: 'pointer', backgroundColor: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'none', borderRadius: '5px', padding: '8px 12px' }}
            >
              Manage Preferences
            </button>
          </div>
        </>
      )}
    </div>
  ) : null;
};

export default CookieBanner;
